/**
 * MediaPage — Steward Admin
 * Upload and manage photos, replay videos, and sponsor logos.
 * Sponsors can be assigned to multiple championships.
 */
import React, { useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import {
  onEvents, onSponsors, onChampionships,
  createSponsor, updateSponsor, deleteSponsor, TITLES,
} from '../services/tripleCrown';
import useChampionship from '../hooks/useChampionship';
import { db } from '../services/firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import S3Upload from '../components/S3Upload';

const mediaCol = collection(db, 'championships', 'data', 'media');

/* ══════════════════════════════════════
   MEDIA UPLOAD SECTION (Photos & Videos)
   ══════════════════════════════════════ */
function MediaSection({ championship }) {
  const [media, setMedia] = useState([]);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [toast, setToast] = useState(null);

  const [uploadType, setUploadType] = useState('photo');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadCaption, setUploadCaption] = useState('');
  const [uploadTitleId, setUploadTitleId] = useState(TITLES[0].id);
  const [uploadEventId, setUploadEventId] = useState('');
  const [uploadUrl, setUploadUrl] = useState('');

  useEffect(() => {
    if (!championship) return;
    const unsub = onEvents(championship.id, setEvents);
    return unsub;
  }, [championship]);

  useEffect(() => {
    if (!championship) return;
    loadMedia();
  }, [championship]);

  const loadMedia = async () => {
    const q = query(mediaCol, orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    setMedia(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const filtered = filter === 'all' ? media : media.filter(m => m.type === filter);

  const handleSaveMedia = async () => {
    if (!uploadUrl) { setToast({ type: 'error', msg: 'Upload a file first' }); return; }
    try {
      await addDoc(mediaCol, {
        type: uploadType, url: uploadUrl,
        title: uploadTitle.trim() || null, caption: uploadCaption.trim() || null,
        titleId: uploadTitleId, event: uploadEventId || null,
        championship: championship.id, createdAt: serverTimestamp(),
      });
      setToast({ type: 'success', msg: 'Media saved!' });
      setShowUpload(false); setUploadUrl(''); setUploadTitle(''); setUploadCaption('');
      loadMedia();
    } catch (err) {
      setToast({ type: 'error', msg: err.message });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this media item?')) return;
    await deleteDoc(doc(mediaCol, id));
    loadMedia();
  };

  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 3000); return () => clearTimeout(t); }
  }, [toast]);

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div className="card-header">
        <h3 className="card-title">Photos & Videos</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setShowUpload(!showUpload)}>
          {showUpload ? 'Cancel' : '+ Upload Media'}
        </button>
      </div>

      {showUpload && (
        <div style={{ padding: '14px 0', borderBottom: '1px solid var(--border)', marginBottom: 14 }}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-select" value={uploadType} onChange={e => setUploadType(e.target.value)}>
                <option value="photo">Photo</option>
                <option value="replay">Replay Video</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Game Title</label>
              <select className="form-select" value={uploadTitleId} onChange={e => setUploadTitleId(e.target.value)}>
                {TITLES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Title / Name</label>
              <input className="form-input" value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} placeholder="e.g. GT7 Phase 1 Highlights" />
            </div>
            <div className="form-group">
              <label className="form-label">Event (optional)</label>
              <select className="form-select" value={uploadEventId} onChange={e => setUploadEventId(e.target.value)}>
                <option value="">No specific event</option>
                {events.map(evt => {
                  const title = TITLES.find(t => t.id === evt.titleId);
                  return (
                    <option key={evt.id} value={evt.id}>
                      {title?.short} — {evt.phaseName || `Phase ${evt.phase}`}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Caption</label>
            <input className="form-input" value={uploadCaption} onChange={e => setUploadCaption(e.target.value)} placeholder="Optional description" />
          </div>

          <div className="form-group">
            <label className="form-label">File</label>
            <S3Upload
              folder={`championships/media/${uploadType}s`}
              accept={uploadType === 'photo' ? 'image/*' : 'video/*'}
              label={`Upload ${uploadType === 'photo' ? 'Photo' : 'Video'}`}
              onUploaded={({ publicUrl }) => setUploadUrl(publicUrl)}
            />
            {uploadUrl && <p className="form-hint" style={{ color: 'var(--success)', marginTop: 4 }}>File uploaded</p>}
          </div>

          <button className="btn btn-primary" onClick={handleSaveMedia} disabled={!uploadUrl}>
            Save Media
          </button>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {['all', 'photo', 'replay'].map(f => (
          <button key={f} className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(f)}>
            {f === 'all' ? 'All' : f === 'photo' ? 'Photos' : 'Videos'}
          </button>
        ))}
        <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center', marginLeft: 'auto' }}>
          {filtered.length} item{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Media grid */}
      {filtered.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No media uploaded yet</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {filtered.map(item => (
            <div key={item.id} style={{
              borderRadius: 'var(--radius-md)', overflow: 'hidden',
              border: '1px solid var(--border)', background: 'var(--bg-input)',
            }}>
              {item.type === 'photo' ? (
                <img src={item.url} alt={item.caption || ''} style={{ width: '100%', height: 140, objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '100%', height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                  <video src={item.url} style={{ maxWidth: '100%', maxHeight: '100%' }} controls />
                </div>
              )}
              <div style={{ padding: '8px 10px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{item.title || item.type}</div>
                {item.caption && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.caption}</div>}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                  <span className="badge badge-teal" style={{ fontSize: 10 }}>
                    {TITLES.find(t => t.id === item.titleId)?.short || item.titleId}
                  </span>
                  <button className="btn btn-danger btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => handleDelete(item.id)}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}

/* ══════════════════════════════════════
   SPONSORS SECTION — with championship assignment
   ══════════════════════════════════════ */
function SponsorsSection() {
  const [sponsors, setSponsors] = useState([]);
  const [championships, setChampionships] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [form, setForm] = useState({ name: '', website: '', tier: 'silver', logoUrl: '', order: 0, championships: [] });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const unsub1 = onSponsors(setSponsors);
    const unsub2 = onChampionships(setChampionships);
    return () => { unsub1(); unsub2(); };
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleChampionship = (champId) => {
    setForm(f => {
      const current = f.championships || [];
      if (current.includes(champId)) {
        return { ...f, championships: current.filter(id => id !== champId) };
      }
      return { ...f, championships: [...current, champId] };
    });
  };

  const resetForm = () => {
    setForm({ name: '', website: '', tier: 'silver', logoUrl: '', order: 0, championships: [] });
    setEditingSponsor(null);
    setShowAdd(false);
  };

  const startEdit = (sponsor) => {
    setForm({
      name: sponsor.name || '',
      website: sponsor.website || '',
      tier: sponsor.tier || 'silver',
      logoUrl: sponsor.logoUrl || '',
      order: sponsor.order || 0,
      championships: sponsor.championships || [],
    });
    setEditingSponsor(sponsor);
    setShowAdd(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const data = {
        name: form.name.trim(),
        website: form.website.trim(),
        tier: form.tier,
        logoUrl: form.logoUrl,
        order: parseInt(form.order) || 0,
        championships: form.championships || [],
      };

      if (editingSponsor) {
        await updateSponsor(editingSponsor.id, data);
        setToast({ type: 'success', msg: 'Sponsor updated!' });
      } else {
        await createSponsor(data);
        setToast({ type: 'success', msg: 'Sponsor added!' });
      }
      resetForm();
    } catch (err) {
      setToast({ type: 'error', msg: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove sponsor "${name}"?`)) return;
    await deleteSponsor(id);
  };

  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 3000); return () => clearTimeout(t); }
  }, [toast]);

  // Helper to resolve championship names
  const getChampNames = (champIds) => {
    if (!champIds || champIds.length === 0) return '—';
    return champIds.map(id => {
      const c = championships.find(ch => ch.id === id);
      return c?.title || id.slice(0, 6);
    }).join(', ');
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Sponsors</h3>
        <button className="btn btn-primary btn-sm" onClick={() => { if (showAdd && !editingSponsor) { resetForm(); } else { resetForm(); setShowAdd(true); } }}>
          {showAdd ? 'Cancel' : '+ Add Sponsor'}
        </button>
      </div>

      {showAdd && (
        <div style={{ padding: '14px 0', borderBottom: '1px solid var(--border)', marginBottom: 14 }}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Sponsor Name *</label>
              <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Company name" />
            </div>
            <div className="form-group">
              <label className="form-label">Website</label>
              <input className="form-input" value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://..." />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Tier</label>
              <select className="form-select" value={form.tier} onChange={e => set('tier', e.target.value)}>
                <option value="platinum">Platinum</option>
                <option value="gold">Gold</option>
                <option value="silver">Silver</option>
                <option value="bronze">Bronze</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Display Order</label>
              <input className="form-input" type="number" value={form.order} onChange={e => set('order', e.target.value)} placeholder="0" />
            </div>
          </div>

          {/* Championship assignment */}
          <div className="form-group">
            <label className="form-label">Assign to Championships</label>
            {championships.length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>No championships created yet</p>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {championships.map(c => {
                  const isSelected = (form.championships || []).includes(c.id);
                  return (
                    <label
                      key={c.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                        padding: '6px 12px', borderRadius: 6, fontSize: 13,
                        border: isSelected ? '1px solid var(--teal)' : '1px solid var(--border)',
                        background: isSelected ? 'var(--teal-soft)' : 'var(--bg-input)',
                        color: isSelected ? 'var(--teal)' : 'var(--text)',
                        transition: 'all 0.15s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleChampionship(c.id)}
                        style={{ display: 'none' }}
                      />
                      <span style={{
                        width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                        border: isSelected ? '2px solid var(--teal)' : '2px solid var(--border-strong)',
                        background: isSelected ? 'var(--teal)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9, color: '#fff', fontWeight: 700,
                      }}>
                        {isSelected ? '✓' : ''}
                      </span>
                      {c.title}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Logo</label>
            <S3Upload
              folder="championships/sponsors"
              accept="image/*"
              label="Upload Logo"
              currentUrl={form.logoUrl}
              onUploaded={({ publicUrl }) => set('logoUrl', publicUrl)}
            />
          </div>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving...' : editingSponsor ? 'Update Sponsor' : 'Add Sponsor'}
          </button>
        </div>
      )}

      {sponsors.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No sponsors added yet</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Name</th>
                <th>Tier</th>
                <th>Championships</th>
                <th>Website</th>
                <th>Order</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sponsors.map(s => {
                const tierBadge = s.tier === 'platinum' ? 'badge-teal'
                  : s.tier === 'gold' ? 'badge-gold'
                  : 'badge-muted';
                return (
                  <tr key={s.id}>
                    <td style={{ width: 50 }}>
                      {s.logoUrl ? (
                        <img src={s.logoUrl} alt="" style={{ width: 40, height: 28, objectFit: 'contain', borderRadius: 4 }} />
                      ) : (
                        <div style={{ width: 40, height: 28, borderRadius: 4, background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'var(--text-muted)' }}>
                          Logo
                        </div>
                      )}
                    </td>
                    <td style={{ fontWeight: 600 }}>{s.name}</td>
                    <td><span className={`badge ${tierBadge}`}>{s.tier}</span></td>
                    <td style={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {getChampNames(s.championships)}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {s.website ? <a href={s.website} target="_blank" rel="noopener noreferrer">{s.website}</a> : '—'}
                    </td>
                    <td>{s.order}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => startEdit(s)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s.id, s.name)}>Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN MEDIA PAGE
   ══════════════════════════════════════ */
export default function MediaPage() {
  const { champ } = useChampionship();

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Media & Sponsors</h1>
          <p className="page-subtitle">Upload photos, replay videos, and manage sponsor logos</p>
        </div>
      </div>

      <MediaSection championship={champ} />
      <SponsorsSection />
    </div>
  );
}
